import React, { createContext, useContext, useState, useCallback } from "react";
import Icon from "../AppIcon";

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "success", duration = 3000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };

    setToasts((prev) => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  );
};

const Toast = ({ toast, onRemove }) => {
  const { id, message, type } = toast;

  const typeStyles = {
    success: {
      bg: "bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-800",
      text: "text-green-700 dark:text-green-400",
      icon: "CheckCircle2",
      iconColor: "text-green-600 dark:text-green-400",
    },
    error: {
      bg: "bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-800",
      text: "text-red-700 dark:text-red-400",
      icon: "XCircle",
      iconColor: "text-red-600 dark:text-red-400",
    },
    info: {
      bg: "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-800",
      text: "text-blue-700 dark:text-blue-400",
      icon: "Info",
      iconColor: "text-blue-600 dark:text-blue-400",
    },
    warning: {
      bg: "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-800",
      text: "text-amber-700 dark:text-amber-400",
      icon: "AlertTriangle",
      iconColor: "text-amber-600 dark:text-amber-400",
    },
  };

  const styles = typeStyles[type] || typeStyles.success;

  return (
    <div
      className={`${styles.bg} border ${styles.text} px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-[400px] pointer-events-auto`}
      style={{
        animation: "slideInRight 0.3s ease-out",
      }}
      role="alert"
    >
      <Icon name={styles.icon} size={20} className={styles.iconColor} />
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={() => onRemove(id)}
        className={`${styles.text} hover:opacity-70 transition-opacity`}
        aria-label="Close toast"
      >
        <Icon name="X" size={16} />
      </button>
    </div>
  );
};
